import React, { useState, useEffect, useRef } from "react";
import {
  FaMapMarkedAlt,
  FaGlobe,
  FaPlane,
  FaCamera,
  FaMountain,
  FaHeart,
} from "react-icons/fa";

const travels = [
  {
    country: "India",
    details: "Uttarakhand (2015), Delhi & Agra (2019, 2021)",
    image:
      "https://images.unsplash.com/photo-1564507592333-c60657eea523?w=800&h=600&fit=crop",
    icon: "🇮🇳",
    color: "from-orange-500 to-red-500",
    highlights: [
      "Taj Mahal",
      "Red Fort",
      "Himalayan Foothills",
      "Cultural Heritage",
    ],
  },
  {
    country: "Thailand & Singapore",
    details: "2016",
    image:
      "https://images.unsplash.com/photo-1552075917-8e1eded32f0d?w=800&h=600&fit=crop",
    icon: "🇹🇭",
    color: "from-green-500 to-teal-500",
    highlights: [
      "Bangkok Temples",
      "Marina Bay",
      "Street Food",
      "Modern Architecture",
    ],
  },
  {
    country: "China",
    details: "Beijing, Chengdu, Great Wall (2017, 2018)",
    image:
      "https://images.unsplash.com/photo-1508804185872-d7badad00f7d?w=800&h=600&fit=crop",
    icon: "🇨🇳",
    color: "from-red-500 to-yellow-500",
    highlights: [
      "Great Wall",
      "Forbidden City",
      "Panda Reserve",
      "Ancient Culture",
    ],
  },
  {
    country: "Bhutan",
    details: "Thimphu, Paro (2018)",
    image:
      "https://images.unsplash.com/photo-1605540436563-5bca919ae766?w=800&h=600&fit=crop",
    icon: "🇧🇹",
    color: "from-blue-500 to-purple-500",
    highlights: [
      "Tiger's Nest",
      "Buddhism",
      "Mountain Valleys",
      "Gross National Happiness",
    ],
  },
  {
    country: "Bangladesh",
    details: "2022",
    image:
      "https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=800&h=600&fit=crop",
    icon: "🇧🇩",
    color: "from-green-600 to-emerald-500",
    highlights: ["Dhaka", "Sundarbans", "River Life", "Cultural Exchange"],
  },
  {
    country: "Europe",
    details:
      "Spain, Portugal, Denmark, Netherlands, Switzerland, France, Germany, Sweden (2021–2024)",
    image:
      "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800&h=600&fit=crop",
    icon: "🇪🇺",
    color: "from-purple-500 to-pink-500",
    highlights: [
      "Alpine Views",
      "Historic Cities",
      "Art Museums",
      "Cultural Diversity",
    ],
  },
  {
    country: "USA",
    details: "Boston, New York, Washington DC, California, etc. (2023)",
    image:
      "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=800&h=600&fit=crop",
    icon: "🇺🇸",
    color: "from-blue-600 to-indigo-600",
    highlights: [
      "Statue of Liberty",
      "Golden Gate",
      "Capitol Hill",
      "Innovation Hubs",
    ],
  },
  {
    country: "Tibet",
    details: "Everest Base Camp, Lhasa (2024)",
    image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
    icon: "🏔️",
    color: "from-cyan-500 to-blue-600",
    highlights: [
      "Everest Base Camp",
      "Potala Palace",
      "High Altitude",
      "Spiritual Journey",
    ],
  },
];

const travelStats = [
  {
    icon: FaGlobe,
    number: "8+",
    label: "Countries",
    description: "Explored across continents",
  },
  {
    icon: FaPlane,
    number: "50+",
    label: "Flights",
    description: "Miles traveled worldwide",
  },
  {
    icon: FaCamera,
    number: "1000+",
    label: "Photos",
    description: "Memories captured",
  },
  {
    icon: FaHeart,
    number: "100+",
    label: "Experiences",
    description: "Life-changing moments",
  },
];

export default function Travel() {
  const [scrollY, setScrollY] = useState(0);
  const [visibleItems, setVisibleItems] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => [...prev, entry.target.dataset.index]);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".travel-item").forEach((item, index) => {
      item.dataset.index = index;
      observer.observe(item);
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900"
      ref={containerRef}
    >
      {/* Hero Section with Parallax */}
      <div className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&h=1080&fit=crop')`,
            transform: `translateY(${scrollY * 0.5}px)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-emerald-900/60 to-slate-900/80"></div>
        </div>

        <div className="relative z-10 text-center max-w-6xl mx-auto px-6">
          <div className="space-y-8">
            <h1 className="text-6xl md:text-8xl font-black text-white mb-6">
              <span className="block">Travel</span>
              <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                Adventures
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              Journey across continents, experiencing diverse cultures and
              creating unforgettable memories. Each destination tells a unique
              story of discovery and connection.
            </p>

            <div className="flex flex-wrap justify-center gap-6 pt-8">
              {travelStats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:border-emerald-500/50 transition-all duration-300 hover:scale-105 text-center group"
                >
                  <div className="text-emerald-400 mb-3 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    <stat.icon className="w-8 h-8" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">
                    {stat.number}
                  </div>
                  <div className="text-lg font-semibold text-emerald-300 mb-1">
                    {stat.label}
                  </div>
                  <div className="text-sm text-gray-300">
                    {stat.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="flex flex-col items-center space-y-2 text-white/60">
            <span className="text-sm font-medium">Explore Destinations</span>
            <div className="w-6 h-10 border-2 border-white/60 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Travel Destinations Grid */}
      <div className="px-6 lg:px-20 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Destinations Explored
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From the bustling streets of Asia to the historic landmarks of
              Europe and America, each journey has shaped my understanding of
              the world.
            </p>
          </div>

          <div className="space-y-8">
            {travels.map((travel, index) => (
              <div
                key={index}
                className={`travel-item group transition-all duration-1000 ${
                  visibleItems.includes(index.toString())
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-12"
                }`}
              >
                <div
                  className={`bg-white/5 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/10 hover:border-emerald-500/50 transition-all duration-500 ${
                    index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                  } flex flex-col lg:flex group-hover:scale-[1.02]`}
                >
                  {/* Image Section with Parallax Effect */}
                  <div className="lg:w-1/2 relative overflow-hidden">
                    <div
                      className="h-80 lg:h-96 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                      style={{
                        backgroundImage: `url(${travel.image})`,
                        transform: `translateY(${
                          scrollY * (0.1 * (index + 1))
                        }px)`,
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                      {/* Country Icon Overlay */}
                      <div className="absolute top-6 left-6 text-6xl group-hover:scale-110 transition-transform duration-300">
                        {travel.icon}
                      </div>

                      {/* Gradient Overlay */}
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${travel.color} opacity-20 group-hover:opacity-30 transition-opacity duration-300`}
                      ></div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-3xl md:text-4xl font-bold text-white group-hover:text-emerald-300 transition-colors duration-300 mb-2">
                          {travel.country}
                        </h3>
                        <div className="flex items-center space-x-2 text-emerald-400 mb-4">
                          <FaMapMarkedAlt className="w-5 h-5" />
                          <span className="text-lg font-medium">
                            {travel.details}
                          </span>
                        </div>
                      </div>

                      {/* Highlights */}
                      <div className="space-y-3">
                        <h4 className="text-lg font-semibold text-gray-300">
                          Key Highlights:
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                          {travel.highlights.map((highlight, hIndex) => (
                            <div
                              key={hIndex}
                              className="flex items-center space-x-2 text-gray-300"
                            >
                              <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                              <span className="text-sm font-medium">
                                {highlight}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="pt-4">
                        <button
                          className={`group/btn inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r ${travel.color} rounded-full font-semibold text-white hover:shadow-2xl hover:shadow-emerald-500/25 transition-all duration-300 hover:scale-105`}
                        >
                          <FaCamera className="w-4 h-4 group-hover/btn:rotate-12 transition-transform duration-300" />
                          <span>View Gallery</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Travel Philosophy Section */}
      <div className="px-6 lg:px-20 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/10 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10"></div>
            <div className="relative z-10">
              <FaMountain className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Travel Philosophy
              </h3>
              <blockquote className="text-xl md:text-2xl text-gray-200 italic leading-relaxed mb-6">
                "Travel is not just about visiting new places; it's about
                opening your mind to new perspectives, building bridges across
                cultures, and discovering the common threads that unite us all
                as human beings."
              </blockquote>
              <div className="w-16 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Background Elements */}
      <div className="fixed inset-0 pointer-events-none opacity-5">
        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-emerald-500/30 to-teal-500/30 rounded-full blur-3xl"
          style={{
            transform: `translate(${scrollY * 0.15}px, ${scrollY * 0.1}px)`,
            left: "10%",
            top: "30%",
          }}
        ></div>
        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-teal-500/30 to-cyan-500/30 rounded-full blur-3xl"
          style={{
            transform: `translate(-${scrollY * 0.15}px, -${scrollY * 0.1}px)`,
            right: "10%",
            bottom: "30%",
          }}
        ></div>
      </div>
    </div>
  );
}
